import { Injectable, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FeatureType, UsersService } from '@goatsports/core/data-access';
import { catchError, distinctUntilChanged, map, of, startWith, switchMap, tap } from 'rxjs';
import { AuthenticationService } from './authentication.service';
import { CurrentUserService } from './current-user.service';

export interface MeProfile {
  firstName: string;
  lastName: string;
  grantedFeatures: FeatureType[];
}

/**
 * Sole owner of the portal's GET /Users/Me self-read — the DB view of the signed-in
 * user, as opposed to {@link CurrentUserService}, which reads the JWT claims.
 * One fetch serves two unrelated consumers: ShellService takes the name fields for
 * the header, and PermissionsService derives feature gating from the granted
 * features. Keep the fetch here; a second owner would double the request.
 */
@Injectable({ providedIn: 'root' })
export class MeProfileService {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly usersService = inject(UsersService);

  // "Settled" is tracked apart from emission: the stream synchronously emits a
  // fallback before the request resolves, so a consumer keying off mere emission
  // would read the fallback's empty grants as an answer.
  private readonly _loaded = signal(false);
  readonly loaded = this._loaded.asReadonly();

  private get fallbackFirstName(): string {
    const fromJwt = this.currentUserService.currentUserFirstName;
    if (fromJwt) return fromJwt;
    // Read the raw JWT claims directly (the local `currentUserEmail` getter
    // String()-wraps the value so a missing claim becomes the literal
    // "undefined"). Some tokens carry `emailaddress` instead of `email`.
    const email = this.currentUserService.currentUserEmail ?? this.currentUserService.currentUserEmailAddress;
    if (!email) return '';
    return email.includes('@') ? email.split('@')[0] : email;
  }

  // Re-fetch whenever the auth token changes user. `startWith(null)` bootstraps
  // the initial emission; `distinctUntilChanged` on the uid keeps silent token
  // refreshes from re-querying the same user, and the inner `startWith` keeps the
  // first emission synchronous (ShellService consumes this through computeds that
  // must resolve at construction time).
  //
  // Uses GET /Users/Me (slim self-only endpoint) rather than GET /Users/{uid} —
  // the latter enforces UserPermissionPolicy and 403s for a Coach reading their
  // own record, which the FE http interceptor turns into an auto-logout. The
  // JWT can't substitute here because given_name/family_name claims aren't
  // reliably populated, so the DB remains the source of truth for the header.
  readonly profile = toSignal(
    this.authenticationService.isTokenReceived().pipe(
      startWith(null),
      map(() => this.currentUserService.currentUserUid),
      distinctUntilChanged(),
      switchMap((uid) => {
        const fallback: MeProfile = {
          firstName: this.fallbackFirstName,
          lastName: this.currentUserService.currentUserLastName,
          grantedFeatures: [],
        };
        if (!uid) {
          this._loaded.set(true);
          return of(fallback);
        }
        return this.usersService.usersMeGet().pipe(
          map((res) => ({
            firstName: res.firstName?.trim() || fallback.firstName,
            lastName: res.lastName?.trim() || fallback.lastName,
            grantedFeatures: res.grantedFeatures ?? [],
          })),
          tap(() => this._loaded.set(true)),
          catchError(() => {
            this._loaded.set(true);
            return of(fallback);
          }),
          startWith(fallback),
        );
      }),
    ),
    { requireSync: true },
  );
}
